// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module createFunnel
 * @package @geenius/analytics/solidjs
 * @description Provides reactive funnel-analysis state for the SolidJS
 * variant so dashboards can evaluate ordered conversion steps against captured
 * events.
 */

import type {
  AnalyticsEvent,
  FunnelResult,
  FunnelStep,
} from "@geenius/analytics";
import { analyzeFunnel } from "@geenius/analytics";
import { createSignal } from "solid-js";

/**
 * Reactive funnel-analysis state returned by {@link createFunnel}.
 *
 * @property result Signal accessor for the latest computed funnel result.
 * @property isAnalyzing Signal accessor for the in-flight analysis flag.
 * @property analyze Computes and stores the next funnel result from raw events.
 */
export interface CreateFunnelReturn {
  result: () => FunnelResult | null;
  isAnalyzing: () => boolean;
  analyze: (events: AnalyticsEvent[]) => FunnelResult;
}

/**
 * Creates funnel-analysis state for a named conversion journey.
 *
 * @param name Human-readable funnel name used in computed result metadata.
 * @param steps Ordered conversion steps that define the funnel.
 * @returns Reactive helpers for running funnel analysis against event streams.
 */
export function createFunnel(
  name: string,
  steps: FunnelStep[],
): CreateFunnelReturn {
  const [result, setResult] = createSignal<FunnelResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = createSignal(false);

  const analyze = (events: AnalyticsEvent[]): FunnelResult => {
    setIsAnalyzing(true);
    const nextResult = analyzeFunnel(events, steps, name);
    setResult(nextResult);
    setIsAnalyzing(false);
    return nextResult;
  };

  return { result, isAnalyzing, analyze };
}
